import React, { useState } from 'react';
import {
  ClozeResponseData,
  OptionSelectResponseAnswer,
} from '../../../../types/quiz';
import { useQuizContext } from '../../../../context/QuizContext';
import { Box } from '@osmosis/react-ui/components/Box';
import { CorrectBanner, SkipQuizButton } from './../../types/utils';
import { P } from '@osmosis/react-ui/components/Text';
import QuizSubmitButtons from '../../QuizSubmitButtons';

type defaultStyle = {
  [key: string]: string;
};

type SelectsAnswer = { index: number; value: string; isCorrect: boolean };

const selectStyles: defaultStyle = {
  m: '2px 4px',
  p: '4px 6px',
  backgroundColor: '#e8e8e8',
  color: '#636363',
  borderRadius: '5px',
  border: 'none',
  fontSize: '14px',
};

/**
 * SelectChoice.
 * @throws No quiz.
 */
const SelectChoice = () => {
  const { currentItem, isAnswered, submitAnswer } = useQuizContext();
  const currentData = currentItem?.data as ClozeResponseData;

  if (currentItem && !('answers' in currentItem.data))
    throw new Error('No answers in this Quiz Type');

  const initialSelectsAnswers = currentData.answers
    .map(
      (value) =>
        value.type === 'select' && {
          index: value.index,
          value: value.options[0],
          isCorrect: value.answer === value.options[0],
        },
    )
    .filter(Boolean) as SelectsAnswer[];

  const [selectsAnswers, setSelectsAnswers] = useState<SelectsAnswer[]>(
    initialSelectsAnswers,
  );

  const isCorrectAnswer = () => {
    for (const answer of currentData.answers) {
      if (answer.type === 'select') {
        return selectsAnswers.every((option) => option.isCorrect);
      }
    }
  };

  const handleSelect = (index: number, value: string) => {
    if (isAnswered) {
      return;
    }
    setSelectsAnswers((prev) => {
      if (prev.find((selectAnswer) => selectAnswer.index === index)) {
        const filteredPrev = prev.filter(
          (selectAnswer) => selectAnswer.index !== index,
        );
        filteredPrev.splice(index, 0, {
          index,
          value,
          isCorrect:
            (
              currentData.answers.find((option) => {
                if (option.type === 'select') {
                  return option.index === index;
                }
              }) as OptionSelectResponseAnswer
            ).answer === value,
        });
        return filteredPrev;
      }
      return prev;
    });
  };

  const getOption = (
    answer: OptionSelectResponseAnswer,
    answerIndex: number,
  ) => {
    if (isAnswered) {
      const selectedOption = selectsAnswers[answerIndex].value;
      return answer.options.map((option) => {
        if (option === answer.answer) {
          const correctOption = `✅ ${option}`;
          return (
            <Box as="option" key={option}>
              {correctOption}
            </Box>
          );
        }
        if (option === selectedOption) {
          const incorrectOption = `❌ ${option}`;
          return (
            <Box as="option" key={option}>
              {incorrectOption}
            </Box>
          );
        }

        return (
          <Box as="option" key={option}>
            {option}
          </Box>
        );
      });
    }

    return answer.options.map((option) => (
      <Box as="option" key={option}>
        {option}
      </Box>
    ));
  };

  return (
    <Box textAlign={'left'}>
      {currentData.answers.map((answer, index) =>
        answer.type === 'text' ? (
          <P
            as="span"
            key={`text-${index}`}
            fontSize={16}
            color="gray50"
            fontWeight={400}
            dangerouslySetInnerHTML={{
              __html: answer.text,
            }}
          />
        ) : (
          <Box
            as="select"
            sx={selectStyles}
            key={`select-${index}`}
            data-testid={`select-${index}`}
            onChange={(e) =>
              handleSelect(answer.index, (e.target as unknown as HTMLSelectElement).value)
            }
          >
            {getOption(answer, answer.index)}
          </Box>
        ),
      )}

      {isAnswered ? (
        <CorrectBanner isCorrectAnswer={!!isCorrectAnswer()} />
      ) : (
        <QuizSubmitButtons
          submit={(c) => submitAnswer(c, !!isCorrectAnswer())}
        />
      )}

      <SkipQuizButton />
    </Box>
  );
};

export default SelectChoice;
